import {
  ApacCandidateFooter,
  ApacCandidateHeader,
  ApacCandidateHeaderFooterLinkProvider,
} from '@seek/apac-candidate-header-footer';
import { UivBanner } from '@seek/uiv-banner';
import { Box, BraidProvider, Page } from 'braid-design-system';
import seekJobs from 'braid-design-system/themes/seekJobs';
import { useEffect, type FC, type ReactElement } from 'react';

import { AuthProvider, useAuth } from 'src/shared/auth/AuthProvider';
import { AUTHENTICATION_TYPES } from 'src/shared/auth/authTypes';
import { getSiteHostname } from 'src/shared/constant';
import { useConfig, useTracking } from 'src/shared/hooks';
import { usePathname } from 'src/shared/hooks/usePathname';
import type {
  LayoutProps,
  MetaProps,
  PageTrackingProps,
} from 'src/shared/types';
import { getDisplayBrand, getQueryString, isWindow } from 'src/utils/helpers';

import { CustomLink } from '../Link/Link';

import { Helmet } from './Helmet';
import { getLanguageSwitcher } from './helper';
import { linkRenderer } from './linkRenderer';

interface StaticPageProps {
  native?: boolean;
  children: ReactElement[] | ReactElement;
  layoutProps: LayoutProps;
  trackingProps: PageTrackingProps;
  meta?: MetaProps;
  metaTags?: ReactElement[] | ReactElement;
}

const StaticPageWebLayout: FC<StaticPageProps> = ({
  children,
  layoutProps,
  trackingProps,
  metaTags,
}) => {
  const { language, brand, country, zone, route, routeName, site, product } =
    useConfig();
  const host = getSiteHostname(product, site);

  const {
    authenticationStatus,
    id,
    emailAddress,
    firstName,
    lastName,
    profilePhotoUrl,
  } = useAuth();

  const pathname = usePathname();

  const { trackView } = useTracking();
  const {
    title,
    returnUrl,
    activeTab,
    noFrame,
    divider,
    minimalHeaderFooter,
    hideFooter,
    enableHreflang,
    isErrorPage,
  } = layoutProps;

  useEffect(() => {
    if (authenticationStatus !== 'pending') {
      const isLoggedIn = authenticationStatus === 'authenticated';
      const authTrackingProps = {
        isLoggedIn,
        ...(isLoggedIn ? { loginId: id } : {}),
      };

      if (isWindow()) {
        window.currentPageTrackingData = {
          zone,
          brand,
          siteLanguage: language,
          siteCountry: country,
          ...trackingProps,
          ...authTrackingProps,
        };
      }
      trackView(trackingProps.currentPage || trackingProps.eventName);
    }
  }, [
    language,
    authenticationStatus,
    brand,
    country,
    id,
    trackView,
    trackingProps,
    zone,
  ]);

  const metaProps = {
    title,
    brandName: getDisplayBrand(brand),
    route,
    routeName,
    host,
    enableHreflang,
    site,
    ...metaTags,
  };

  return (
    <Page
      footer={
        !noFrame &&
        !hideFooter && (
          <ApacCandidateHeaderFooterLinkProvider value={linkRenderer}>
            <ApacCandidateFooter
              authenticationStatus={authenticationStatus}
              isExpandedVersion={!minimalHeaderFooter}
            />
          </ApacCandidateHeaderFooterLinkProvider>
        )
      }
      footerPosition={isErrorPage ? 'belowFold' : undefined}
    >
      {!noFrame && (
        <ApacCandidateHeaderFooterLinkProvider value={linkRenderer}>
          <ApacCandidateHeader
            activePrimaryTab={activeTab}
            authenticationStatus={
              minimalHeaderFooter
                ? AUTHENTICATION_TYPES.AUTH_PENDING
                : authenticationStatus
            }
            divider={divider}
            languageSwitcher={getLanguageSwitcher(pathname, country)}
            leanHeader={minimalHeaderFooter}
            returnUrlForPostAuth={returnUrl}
            userProfile={{
              email: emailAddress,
              firstName,
              lastName,
              profilePhotoUrl,
            }}
          />
        </ApacCandidateHeaderFooterLinkProvider>
      )}
      <Helmet {...metaProps} />
      <div>{children}</div>
    </Page>
  );
};

const StaticPageLayout: FC<StaticPageProps> = ({
  native = false,
  children,
  layoutProps,
  trackingProps,
  meta = {},
}) => {
  const { branch, version } = useConfig();
  const metaTags = Object.keys(meta)
    .map((key) => ({ name: key, content: meta[key] }))
    .map((item) => (
      <meta
        key={item.name}
        name={item.name}
        property={item.name}
        content={item.content}
      />
    ));

  const nativeEmbed = Boolean(getQueryString('native-embed'));
  if (nativeEmbed || native) {
    return <Box>{children}</Box>;
  }

  const { minimalHeaderFooter } = layoutProps;

  return (
    <AuthProvider authBypass={Boolean(minimalHeaderFooter)}>
      <BraidProvider theme={seekJobs} linkComponent={CustomLink}>
        <UivBanner
          applicationName="wpt-static-pages"
          branch={branch}
          version={version}
        />
        <StaticPageWebLayout
          layoutProps={layoutProps}
          trackingProps={trackingProps}
          metaTags={metaTags}
        >
          {children}
        </StaticPageWebLayout>
      </BraidProvider>
    </AuthProvider>
  );
};

export default StaticPageLayout;
